import { validateEffects } from './effects';
import { validateSkills } from './skills';
import { validateNames } from './names';
import { validateHome, validateHomeSkills, validateInventory } from './homebase';
import {
  BOSS_ARENA_IDS,
  BOSS_ATTACK_KINDS,
  BOSS_PLATE_ZONES,
  BOSS_STATUSES,
  bossAttackDefaults,
  bossDefaults,
  bossPhaseDefaults,
  bossPlateDefaults,
  withDefaults,
} from '../teamsDefaults';
import {
  type BossDef,
  type LootTiersDef,
  type Bundle,
  type Stack,
  type BundleKey,
  type Issue,
  ARMOR_MAX_REDUCTION,
  ARMOR_LIMITS,
  ARMOR_FIELD_DEFAULTS,
  MAX_ITEM_VALUE,
  type ArmorDef,
  type ItemDef,
  type ThrowableDef,
  THROWABLE_KINDS,
  THROWABLE_LIMITS,
  NOISE_ACTIONS,
  REQUIRED_SECTIONS,
  TEAMS_SECTIONS,
  ZOMBIE_NUMBERS,
  RAID_CHANCES,
  RAID_NUMBERS,
  HEADSHOT_NUMBERS,
  HEADSHOT_CHANCES,
  HEADSHOT_POSITIVE,
  STEALTH_NUMBERS,
  STEALTH_POSITIVE,
  STEALTH_CHANCES,
  STEALTH_DEGREES,
  NOISE_METER_NUMBERS,
  NOISE_METER_POSITIVE,
  NOISE_METER_CHANCES,
  WEAPON_STEALTH_DEFAULTS,
  HELD_MODELS,
  PIERCE_DEFAULTS,
  FISTS_ID,
  HORDE_NUMBERS,
  SURVIVAL_NUMBERS,
  INT_MAX,
  HEADSHOT_DEFAULTS,
  ZOMBIE_STEALTH_DEFAULTS,
  STEALTH_DEFAULTS,
  NOISE_METER_DEFAULTS,
  THROWABLE_FIELD_DEFAULTS,
  stripUndefined,
} from './types';
import { isBlank, num, isChance, isSlug, type Ids } from './common';
import { validateBases } from './bases';
import { validateTeams } from './teams';
import { validateCombatHud } from './combatHud';
import { validateTriggers } from './triggers';
import { validateConditions } from './conditions';
import { validateBeats } from './beats';
import { validatePanels } from './panels';
import { validateScenarios } from './scenarios';
import { validateChance } from './chance';
import { validateRescues } from './rescue';
import { validateOccupied } from './occupied';
import { validateHoldouts } from './holdout';
import { validateTech } from './tech';
import { validateKeyParts } from './keyparts';
import { validateArena } from './arena';
import { validateOverwatch } from './overwatch';
import { validateStoryIssues } from './story';
import { isThemeCategory, validateThemes } from './themes';
import { validateHealth } from './health';
import { validateTutorial } from './tutorial';
import { validateWind, windWarnings } from './wind';
import { validateWeaponAudio } from './audio';

/**
 * A TypeScript port of `ContentValidator.Validate` (packages/shared-cs/Runtime/Content/ContentValidator.cs) and
 * `LootRoller.Problems`, plus a few client-only warnings. Error messages match the server's wording so the Publish
 * page and the server agree. The server stays authoritative: it re-validates on publish.
 */
export function validateBundle(b: Bundle): Issue[] {
  const issues: Issue[] = [];
  const err = (section: BundleKey, index: number | undefined, path: string, message: string) =>
    issues.push({ section, index, path, message, severity: 'error' });
  const warn = (section: BundleKey, index: number | undefined, path: string, message: string) =>
    issues.push({ section, index, path, message, severity: 'warning' });

  for (const key of REQUIRED_SECTIONS)
    if (b[key] == null) err(key, undefined, '', `Bundle is missing '${key}'.`);
  // Bases (ADR 0030) come right after survival on the server; absent = the C# defaults, only explicit null is missing.
  if (b.bases === null) err('bases', undefined, '', "Bundle is missing 'bases'.");
  for (const key of TEAMS_SECTIONS) {
    if (b[key] === null) err(key, undefined, '', `Bundle is missing '${key}'.`);
    // Mirrors ContentValidator.Section for the one new collection.
    if (key === 'survivorTraits')
      (b.survivorTraits ?? []).forEach((t, i) => {
        if (t == null) err(key, i, '', "Bundle has an empty entry in 'survivorTraits'.");
      });
  }

  const itemsList = b.items ?? [];
  const tablesList = b.lootTables ?? [];
  const zombiesList = b.zombies ?? [];
  const modulesList = b.baseModules ?? [];

  /**
   * Mirrors `Ids`. Null entries are skipped (the server reports them as empty entries first). With `base`, the list
   * is nested inside an object section (e.g. `teamRules.fatigueTiers`) and issues get the path `base.i.id`.
   */
  const ids: Ids = (section, list, kind, base) => {
    const set = new Set<string>();
    list.forEach((x, i) => {
      if (x == null) return;
      const index = base === undefined ? i : undefined;
      const path = base === undefined ? 'id' : `${base}.${i}.id`;
      const v = x.id ?? '';
      if (isBlank(v)) err(section, index, path, `A ${kind} has no id.`);
      else if (set.has(v)) err(section, index, path, `Duplicate ${kind} id '${v}'.`);
      else set.add(v);
    });
    return set;
  };

  const items = ids('items', itemsList, 'item');
  const tables = ids('lootTables', tablesList, 'loot table');
  const zombies = ids('zombies', zombiesList, 'zombie');
  const modules = ids('baseModules', modulesList, 'base module');
  ids('recipes', b.recipes ?? [], 'recipe');
  ids('roomTemplates', b.roomTemplates ?? [], 'room template');
  const categories = ids('categories', b.categories ?? [], 'category');
  // Recruit roles are retired (card 11, ADR 0044): background ids stand in where content names a recruit's kind.
  const roles = new Set((b.backgrounds ?? []).flatMap((g) => (g?.id ? [g.id] : [])));
  const traitsList = b.survivorTraits ?? [];
  const traits = ids('survivorTraits', traitsList, 'survivor trait');

  const stacks = (
    section: BundleKey,
    index: number | undefined,
    base: string,
    list: (Stack | null)[] | null | undefined,
    where: string,
  ) =>
    (list ?? []).forEach((s: Stack | null, j) => {
      if (s == null) {
        err(section, index, `${base}.${j}`, `${where} has an empty stack.`);
        return;
      }
      const id = s.itemId ?? '';
      if (!items.has(id))
        err(section, index, `${base}.${j}.itemId`, `${where} has unknown item '${id}'.`);
      if (num(s.quantity) <= 0)
        err(section, index, `${base}.${j}.quantity`, `${where} '${id}' needs a positive quantity.`);
    });

  const notNegative = (
    section: BundleKey,
    index: number | undefined,
    path: string,
    value: number | null | undefined,
    where: string,
  ) => {
    if (num(value) < 0) err(section, index, path, `${where} ${path} is negative.`);
  };

  /** Mirrors `ContentValidator.Armor`: the raid overhaul 06 armor ranges, something to protect with, and the repair. */
  const armor = (i: number, id: string, a: ArmorDef) => {
    const L = ARMOR_LIMITS;
    const bonusHealth = num(a.bonusHealth);
    const blocks = num(a.blocksPerRaid);
    const guard = a.blockGuardS ?? ARMOR_FIELD_DEFAULTS.blockGuardS;
    const speed = a.moveSpeedMult ?? ARMOR_FIELD_DEFAULTS.moveSpeedMult;
    const noise = num(a.movementNoiseAddM);
    const where = `Armor '${id}'`;
    if (bonusHealth < 0 || bonusHealth > L.maxBonusHealth)
      err('items', i, 'armor.bonusHealth', `${where} needs bonusHealth in 0..${L.maxBonusHealth}.`);
    if (blocks < 0 || blocks > L.maxBlocksPerRaid)
      err(
        'items',
        i,
        'armor.blocksPerRaid',
        `${where} needs blocksPerRaid in 0..${L.maxBlocksPerRaid}.`,
      );
    if (guard < 0 || guard > L.maxBlockGuardS)
      err('items', i, 'armor.blockGuardS', `${where} needs blockGuardS in 0..${L.maxBlockGuardS}.`);
    if (speed < L.minMoveSpeedMult || speed > 1)
      err(
        'items',
        i,
        'armor.moveSpeedMult',
        `${where} needs moveSpeedMult in ${L.minMoveSpeedMult}..1.`,
      );
    if (noise < 0 || noise > L.maxMovementNoiseAddM)
      err(
        'items',
        i,
        'armor.movementNoiseAddM',
        `${where} needs movementNoiseAddM in 0..${L.maxMovementNoiseAddM}.`,
      );
    if (bonusHealth <= 0 && blocks <= 0 && num(a.reduction) <= 0)
      err('items', i, 'armor', `${where} needs padding, blocks or reduction.`);

    const r = a.repair;
    if (r == null) return;
    if (num(r.restores) < 1)
      err('items', i, 'armor.repair.restores', `${where} repair needs restores >= 1.`);
    if ((r.inputs ?? []).length === 0)
      err('items', i, 'armor.repair.inputs', `${where} repair needs inputs.`);
    stacks('items', i, 'armor.repair.inputs', r.inputs, `${where} repair input`);
    if (r.requiredModuleId == null || r.requiredModuleId === '') return;
    if (!modules.has(r.requiredModuleId)) {
      err(
        'items',
        i,
        'armor.repair.requiredModuleId',
        `${where} repair needs unknown module '${r.requiredModuleId}'.`,
      );
      return;
    }
    const levels = modulesList.find((m) => m.id === r.requiredModuleId)?.levels?.length ?? 0;
    const lvl = num(r.requiredModuleLevel);
    if (lvl < 1 || lvl > levels)
      err(
        'items',
        i,
        'armor.repair.requiredModuleLevel',
        `${where} repair needs level ${lvl} of module '${r.requiredModuleId}', which has levels 1..${levels}.`,
      );
  };

  (itemsList as ItemDef[]).forEach((it, i) => {
    const id = it.id ?? '';
    if (num(it.weightKg) < 0) err('items', i, 'weightKg', `Item '${id}' has a negative weight.`);
    if (num(it.energy) < 0) err('items', i, 'energy', `Item '${id}' has negative energy.`);
    if (num(it.stackSize) < 1) err('items', i, 'stackSize', `Item '${id}' needs stackSize >= 1.`);
    if (id === FISTS_ID) err('items', i, 'id', `Item id '${FISTS_ID}' is reserved for bare hands.`);
    const ammo = it.weapon?.ammoItemId;
    if (ammo != null && !items.has(ammo))
      err('items', i, 'weapon.ammoItemId', `Weapon '${id}' uses unknown ammo '${ammo}'.`);
    if (it.weapon && !isChance(num(it.weapon.recoverChance)))
      err('items', i, 'weapon.recoverChance', `Weapon '${id}' needs recoverChance in 0..1.`);
    if (it.armor) {
      const r = num(it.armor.reduction);
      if (r < 0 || r > ARMOR_MAX_REDUCTION)
        err(
          'items',
          i,
          'armor.reduction',
          `Armor '${id}' needs reduction in 0..${ARMOR_MAX_REDUCTION}.`,
        );
      if (num(it.armor.durability) < 1)
        err('items', i, 'armor.durability', `Armor '${id}' needs durability >= 1.`);
      if (it.weapon) err('items', i, 'armor', `Item '${id}' can't be both a weapon and armor.`);
      armor(i, id, it.armor);
    }
    const value = num(it.value);
    if (value < 0 || value > MAX_ITEM_VALUE)
      err(
        'items',
        i,
        'value',
        `Item '${id}' needs value in 0..${MAX_ITEM_VALUE} (0 = the category default).`,
      );
    // Client-only sanity checks.
    if (it.noiseRadiusM != null && it.noiseRadiusM < 0)
      warn('items', i, 'noiseRadiusM', 'Noise radius should not be negative.');
    if (it.carryBonusKg != null && it.carryBonusKg < 0)
      warn('items', i, 'carryBonusKg', 'Carry bonus should not be negative.');
    if (num(it.heal) < 0) warn('items', i, 'heal', 'Heal should not be negative.');
    if (it.weapon) {
      const w = it.weapon;
      for (const k of [
        'damage',
        'rangeM',
        'fireIntervalS',
        'magazineSize',
        'reloadS',
        'spreadDeg',
        'meleeArcDeg',
      ] as const)
        if (num(w[k]) < 0) warn('items', i, `weapon.${k}`, 'Should not be negative.');
      if (num(w.pellets) < 1)
        warn('items', i, 'weapon.pellets', 'A weapon fires at least 1 pellet.');
      if (w.durabilityShots != null && w.durabilityShots < 1)
        warn('items', i, 'weapon.durabilityShots', 'Leave empty for no wear, or use 1 or more.');
      if (ammo != null && items.has(ammo)) {
        const ammoItem = itemsList.find((x) => x.id === ammo);
        if (ammoItem && ammoItem.category !== 'ammo')
          warn('items', i, 'weapon.ammoItemId', `'${ammo}' is not in the ammo category.`);
      }
    }
  });

  tablesList.forEach((t, i) => {
    const tid = t.id ?? '';
    const prefix = `Loot table '${tid}'`;
    if (t.entries == null) {
      err('lootTables', i, 'entries', `${prefix} has no entries list.`);
      return;
    }
    // Mirrors LootRoller.Problems: at most one problem per entry, then the positive weight check.
    let anyWeight = false;
    t.entries.forEach((e, j) => {
      const eid = e.itemId ?? '';
      if (isBlank(eid))
        err('lootTables', i, `entries.${j}.itemId`, `${prefix} has an entry with no itemId.`);
      else if (num(e.weight) < 0)
        err(
          'lootTables',
          i,
          `entries.${j}.weight`,
          `${prefix} entry '${eid}' has a negative weight.`,
        );
      else if (num(e.min) < 1 || num(e.max) < num(e.min))
        err('lootTables', i, `entries.${j}.max`, `${prefix} entry '${eid}' needs 1 <= min <= max.`);
      else if (num(e.max) === INT_MAX)
        err('lootTables', i, `entries.${j}.max`, `${prefix} entry '${eid}' max is too large.`);
      if (num(e.weight) > 0) anyWeight = true;
    });
    if (!anyWeight)
      err('lootTables', i, 'entries', `${prefix} needs at least one entry with a positive weight.`);
    t.entries.forEach((e, j) => {
      if (!items.has(e.itemId ?? ''))
        err(
          'lootTables',
          i,
          `entries.${j}.itemId`,
          `${prefix} has unknown item '${e.itemId ?? ''}'.`,
        );
    });
  });

  (b.recipes ?? []).forEach((r, i) => {
    const rid = r.id ?? '';
    if ((r.inputs ?? []).length === 0 || (r.outputs ?? []).length === 0)
      err(
        'recipes',
        i,
        (r.inputs ?? []).length === 0 ? 'inputs' : 'outputs',
        `Recipe '${rid}' needs inputs and outputs.`,
      );
    stacks('recipes', i, 'inputs', r.inputs, `Recipe '${rid}' input`);
    stacks('recipes', i, 'outputs', r.outputs, `Recipe '${rid}' output`);
    if (!isBlank(r.requiredModuleId)) {
      if (!modules.has(r.requiredModuleId!))
        err(
          'recipes',
          i,
          'requiredModuleId',
          `Recipe '${rid}' needs unknown module '${r.requiredModuleId}'.`,
        );
      else {
        const levels = modulesList.find((m) => m.id === r.requiredModuleId)?.levels?.length ?? 0;
        const lvl = num(r.requiredModuleLevel);
        if (lvl < 1 || lvl > levels)
          err(
            'recipes',
            i,
            'requiredModuleLevel',
            `Recipe '${rid}' needs level ${lvl} of module '${r.requiredModuleId}', which has levels 1..${levels}.`,
          );
      }
    }
  });

  modulesList.forEach((m, i) => {
    const mid = m.id ?? '';
    if ((m.levels ?? []).length === 0)
      err('baseModules', i, 'levels', `Module '${mid}' has no levels.`);
    (m.levels ?? []).forEach((l, j) => {
      stacks(
        'baseModules',
        i,
        `levels.${j}.cost`,
        l.cost,
        `Module '${mid}' level ${num(l.level)} cost`,
      );
      if (num(l.level) !== j + 1)
        err(
          'baseModules',
          i,
          `levels.${j}.level`,
          `Module '${mid}' needs level ${j + 1} in position ${j + 1} (levels are in order).`,
        );
      const recruit = num(l.recruitChanceBonus);
      if (!(recruit >= 0 && recruit <= 1))
        err(
          'baseModules',
          i,
          `levels.${j}.recruitChanceBonus`,
          `Module '${mid}' level ${num(l.level)} needs recruitChanceBonus in 0..1.`,
        );
      const repair = num(l.repairBonus);
      if (!(repair >= 0 && repair <= 3))
        err(
          'baseModules',
          i,
          `levels.${j}.repairBonus`,
          `Module '${mid}' level ${num(l.level)} needs repairBonus in 0..3.`,
        );
      for (const k of ['buildSeconds', 'storageBonusKg', 'waterPerDay', 'healPerDay'] as const)
        if (num(l[k]) < 0) warn('baseModules', i, `levels.${j}.${k}`, 'Should not be negative.');
    });
  });

  (b.categories ?? []).forEach((c, i) => {
    const cid = c.id ?? '';
    if (!tables.has(c.lootTableId ?? ''))
      err(
        'categories',
        i,
        'lootTableId',
        `Category '${cid}' uses unknown loot table '${c.lootTableId ?? ''}'.`,
      );
    (c.likelyLoot ?? []).forEach((id, j) => {
      if (!items.has(id))
        err(
          'categories',
          i,
          `likelyLoot.${j}`,
          `Category '${cid}' likely loot has unknown item '${id}'.`,
        );
    });
    const d = num(c.difficulty);
    if (d < 1 || d > 5) warn('categories', i, 'difficulty', 'Difficulty is 1 to 5.');
    if (num(c.lootRollsPerRoom) < 0)
      warn('categories', i, 'lootRollsPerRoom', 'Should not be negative.');
    (c.osmTags ?? []).forEach((tag, j) => {
      if (!/^[^=\s]+=[^=]+$/.test(tag))
        warn('categories', i, `osmTags.${j}`, `'${tag}' should look like key=value or key=*.`);
    });
  });

  (b.roomTemplates ?? []).forEach((r, i) => {
    const cat = r.category ?? '';
    if (cat !== 'shared' && !categories.has(cat) && !isThemeCategory(b, cat))
      // + theme:<id> (ADR 0052)
      err(
        'roomTemplates',
        i,
        'category',
        `Room template '${r.id ?? ''}' uses unknown category '${cat}' (use 'shared' or a building category).`,
      );
  });

  zombiesList.forEach((z, i) => {
    for (const k of ZOMBIE_NUMBERS) notNegative('zombies', i, k, z[k], `Zombie '${z.id ?? ''}'`);
    const hp = num(z.maxHealth);
    if (hp >= 0 && hp < 1) warn('zombies', i, 'maxHealth', 'A zombie needs at least 1 HP.');
  });

  const raid = b.raidRules;
  if (raid) {
    if (raid.zombieSpawnWeights == null)
      err(
        'raidRules',
        undefined,
        'zombieSpawnWeights',
        "Raid rules are missing 'zombieSpawnWeights'.",
      );
    (raid.zombieSpawnWeights ?? []).forEach((w, j) => {
      const zid = w.zombieId ?? '';
      if (!zombies.has(zid))
        err(
          'raidRules',
          undefined,
          `zombieSpawnWeights.${j}.zombieId`,
          `Spawn weight uses unknown zombie '${zid}'.`,
        );
      if (num(w.weight) < 0)
        err(
          'raidRules',
          undefined,
          `zombieSpawnWeights.${j}.weight`,
          `Spawn weight for zombie '${zid}' is negative.`,
        );
    });
    for (const k of RAID_CHANCES)
      if (!isChance(num(raid[k]))) err('raidRules', undefined, k, `Raid rules need ${k} in 0..1.`);
    for (const k of RAID_NUMBERS) notNegative('raidRules', undefined, k, raid[k], 'Raid rules');
    // Fog of war (ADR 0026; absent = the C# default).
    if (raid.visionRadiusM != null && !(raid.visionRadiusM >= 1 && raid.visionRadiusM <= 30))
      err('raidRules', undefined, 'visionRadiusM', 'Raid rules need visionRadiusM in 1..30.');
    notNegative(
      'raidRules',
      undefined,
      'ghostMarkerSeconds',
      raid.ghostMarkerSeconds,
      'Raid rules',
    );
    // Loot feed tiers (Wave 1; absent or null = the C# defaults).
    const tiers = (raid as { lootTiers?: LootTiersDef | null }).lootTiers;
    if (tiers) {
      const good = num(tiers.good);
      const rare = num(tiers.rare);
      const prize = num(tiers.prize);
      if (!(good > 0 && good <= rare && rare <= prize))
        err(
          'raidRules',
          undefined,
          'lootTiers',
          'Raid rules need lootTiers with 0 < good <= rare <= prize.',
        );
    }
    // Bare hands (absent = the server's default; only an explicit null is missing).
    if (raid.unarmed === null)
      err('raidRules', undefined, 'unarmed', "Raid rules are missing 'unarmed'.");
    else if (raid.unarmed !== undefined) {
      const u = raid.unarmed;
      if (num(u.magazineSize) !== 0 || u.ammoItemId != null)
        err(
          'raidRules',
          undefined,
          'unarmed',
          'Raid rules need unarmed to be melee (no ammo, magazineSize 0).',
        );
      if (num(u.damage) < 1)
        err(
          'raidRules',
          undefined,
          'unarmed.damage',
          'Raid rules unarmed.damage must be at least 1.',
        );
      if (!(num(u.rangeM) > 0))
        err('raidRules', undefined, 'unarmed.rangeM', 'Raid rules unarmed.rangeM must be above 0.');
      if (!(num(u.fireIntervalS) > 0))
        err(
          'raidRules',
          undefined,
          'unarmed.fireIntervalS',
          'Raid rules unarmed.fireIntervalS must be above 0.',
        );
      notNegative('raidRules', undefined, 'unarmed.meleeArcDeg', u.meleeArcDeg, 'Raid rules');
    }
    if (raid.horde == null) err('raidRules', undefined, 'horde', "Raid rules are missing 'horde'.");
    else
      for (const k of HORDE_NUMBERS)
        notNegative('raidRules', undefined, `horde.${k}`, raid.horde[k], 'Raid rules');
  }

  // Headshots (shared-cs ContentValidator.Combat.cs). Absent fields are the C# defaults.
  // The new weapons' fields (raid overhaul 01 C.2) follow each weapon's headshot check, as on the server.
  itemsList.forEach((it, i) => {
    if (it.weapon && !isChance(num(it.weapon.headshotChance)))
      err(
        'items',
        i,
        'weapon.headshotChance',
        `Weapon '${it.id ?? ''}' needs headshotChance in 0..1.`,
      );
    if (it.weapon) {
      const w = it.weapon;
      const id = it.id ?? '';
      const pierce = w.pierce ?? PIERCE_DEFAULTS.pierce;
      const melee = num(w.magazineSize) === 0 && w.ammoItemId == null;
      if (pierce < 0) err('items', i, 'weapon.pierce', `Weapon '${id}' pierce can't be negative.`);
      else if (pierce > 0 && melee)
        err('items', i, 'weapon.pierce', `Weapon '${id}' is melee and can't pierce.`);
      // ADR 0066 B pass 1: a manual action (bolt, pump, draw, cock) cycles a ranged weapon only (x combat.cycle).
      if ((w as { manualAction?: boolean }).manualAction && melee)
        err(
          'items',
          i,
          'weapon.manualAction',
          `Weapon '${id}' is melee, so it can't be manualAction (a bolt, pump, draw or cock is ranged only).`,
        );
      if (!isChance(w.pierceFalloff ?? PIERCE_DEFAULTS.pierceFalloff))
        err('items', i, 'weapon.pierceFalloff', `Weapon '${id}' needs pierceFalloff in 0..1.`);
      if ((w.knockbackM ?? PIERCE_DEFAULTS.knockbackM) < 0)
        err('items', i, 'weapon.knockbackM', `Weapon '${id}' knockbackM can't be negative.`);
      if (w.heldModel != null && !(HELD_MODELS as readonly string[]).includes(w.heldModel))
        err(
          'items',
          i,
          'weapon.heldModel',
          `Weapon '${id}' has unknown heldModel '${w.heldModel}' (use ${HELD_MODELS.join(', ')}).`,
        );
    }
  });
  zombiesList.forEach((z, i) => {
    if (!((z.headshotMult ?? 1) > 0))
      err('zombies', i, 'headshotMult', `Zombie '${z.id ?? ''}' headshotMult must be above 0.`);
    if (!isChance(z.knockbackResist ?? PIERCE_DEFAULTS.knockbackResist))
      err('zombies', i, 'knockbackResist', `Zombie '${z.id ?? ''}' needs knockbackResist in 0..1.`);
  });
  if (raid) {
    if (raid.unarmed && !isChance(num(raid.unarmed.headshotChance)))
      err(
        'raidRules',
        undefined,
        'unarmed.headshotChance',
        'Raid rules need unarmed.headshotChance in 0..1.',
      );
    if (raid.headshot === null)
      err('raidRules', undefined, 'headshot', "Raid rules are missing 'headshot'.");
    else if (raid.headshot !== undefined) {
      const h = { ...HEADSHOT_DEFAULTS, ...stripUndefined(raid.headshot) };
      for (const k of HEADSHOT_NUMBERS) {
        const v = h[k];
        const path = `headshot.${k}`;
        if (HEADSHOT_CHANCES.includes(k as (typeof HEADSHOT_CHANCES)[number])) {
          if (!isChance(num(v)))
            err('raidRules', undefined, path, `Raid rules need ${path} in 0..1.`);
        } else if (HEADSHOT_POSITIVE.includes(k as (typeof HEADSHOT_POSITIVE)[number])) {
          if (!(num(v) > 0))
            err('raidRules', undefined, path, `Raid rules ${path} must be above 0.`);
        } else notNegative('raidRules', undefined, path, v, 'Raid rules');
      }
    }
  }

  // Stealth (shared-cs ContentValidator.Combat.cs `Stealth`, right after the headshots). Absent fields are the C# defaults.
  itemsList.forEach((it, i) => {
    const w = it.weapon;
    if (!w) return;
    const where = `Weapon '${it.id ?? ''}'`;
    const killS = w.stealthKillS ?? WEAPON_STEALTH_DEFAULTS.stealthKillS;
    if (w.stealthKill ?? WEAPON_STEALTH_DEFAULTS.stealthKill) {
      if (!(killS > 0))
        err('items', i, 'weapon.stealthKillS', `${where} stealthKillS must be above 0.`);
    } else if (killS < 0)
      err('items', i, 'weapon.stealthKillS', `${where} stealthKillS is negative.`);
    if ((w.stealthKillNoiseM ?? WEAPON_STEALTH_DEFAULTS.stealthKillNoiseM) < 0)
      err('items', i, 'weapon.stealthKillNoiseM', `${where} stealthKillNoiseM is negative.`);
  });
  zombiesList.forEach((zd, i) => {
    const z = { ...ZOMBIE_STEALTH_DEFAULTS, ...stripUndefined(zd) };
    const where = `Zombie '${zd.id ?? ''}'`;
    const e = (path: string, message: string) => err('zombies', i, path, message);
    if (!(z.sightHalfAngleDeg > 0 && z.sightHalfAngleDeg <= 180))
      e('sightHalfAngleDeg', `${where} needs sightHalfAngleDeg above 0 and at most 180.`);
    if (!(z.noticeNearS > 0)) e('noticeNearS', `${where} noticeNearS must be above 0.`);
    if (z.noticeFarS < z.noticeNearS)
      e('noticeFarS', `${where} noticeFarS must be at least noticeNearS.`);
    if (!(z.sweepDeg >= 0 && z.sweepDeg <= 180))
      e('sweepDeg', `${where} needs sweepDeg in 0..180.`);
    if (!(z.sweepPeriodMinS > 0)) e('sweepPeriodMinS', `${where} sweepPeriodMinS must be above 0.`);
    if (z.sweepPeriodMaxS < z.sweepPeriodMinS)
      e('sweepPeriodMaxS', `${where} sweepPeriodMaxS must be at least sweepPeriodMinS.`);
    if (z.stealthKillS < 0) e('stealthKillS', `${where} stealthKillS is negative.`);
    if (z.stealthKillNoiseM < 0) e('stealthKillNoiseM', `${where} stealthKillNoiseM is negative.`);
  });
  if (raid) {
    if (raid.stealth === null)
      err('raidRules', undefined, 'stealth', "Raid rules are missing 'stealth'.");
    else {
      const st = { ...STEALTH_DEFAULTS, ...stripUndefined(raid.stealth ?? {}) };
      for (const k of STEALTH_NUMBERS) {
        const v = num(st[k]);
        const path = `stealth.${k}`;
        if (STEALTH_POSITIVE.includes(k)) {
          if (!(v > 0)) err('raidRules', undefined, path, `Raid rules ${path} must be above 0.`);
        } else if (STEALTH_CHANCES.includes(k)) {
          if (!isChance(v)) err('raidRules', undefined, path, `Raid rules need ${path} in 0..1.`);
        } else if (STEALTH_DEGREES.includes(k)) {
          if (!(v >= 0 && v <= 180))
            err('raidRules', undefined, path, `Raid rules need ${path} in 0..180.`);
        } else if (k === 'alertedHalfAngleDeg') {
          if (!(v > 0 && v <= 180))
            err('raidRules', undefined, path, `Raid rules need ${path} above 0 and at most 180.`);
        } else notNegative('raidRules', undefined, path, v, 'Raid rules');
      }
    }
  }

  // The noise meter (shared-cs ContentValidator.Combat.cs `NoiseMeterRules`, right after the stealth checks). Absent fields
  // are the C# defaults; only an explicit null is missing.
  if (raid) {
    if (raid.noiseMeter === null)
      err('raidRules', undefined, 'noiseMeter', "Raid rules are missing 'noiseMeter'.");
    else {
      const nm = { ...NOISE_METER_DEFAULTS, ...stripUndefined(raid.noiseMeter ?? {}) };
      for (const k of NOISE_METER_NUMBERS) {
        const v = num(nm[k]);
        const path = `noiseMeter.${k}`;
        if (NOISE_METER_POSITIVE.includes(k)) {
          if (!(v > 0)) err('raidRules', undefined, path, `Raid rules ${path} must be above 0.`);
        } else if (NOISE_METER_CHANCES.includes(k)) {
          if (!isChance(v)) err('raidRules', undefined, path, `Raid rules need ${path} in 0..1.`);
        } else if (k === 'brokenHordeFillMult') {
          if (!(v >= 0 && v <= 5))
            err('raidRules', undefined, path, `Raid rules need ${path} in 0..5.`);
        } else if (k === 'driftZombies') {
          if (!(v >= 0 && v <= 10))
            err('raidRules', undefined, path, `Raid rules need ${path} in 0..10.`);
        } else notNegative('raidRules', undefined, path, v, 'Raid rules');
        if (k === 'hordeWakeAt' && !(num(nm.silenceBrokenAt) < v))
          err(
            'raidRules',
            undefined,
            'noiseMeter.silenceBrokenAt',
            'Raid rules noiseMeter.silenceBrokenAt must be below noiseMeter.hordeWakeAt.',
          );
      }
    }
  }

  // Throwables (shared-cs ContentValidator.Throwables.cs, right after the stealth checks). Absent fields are the C# defaults.
  itemsList.forEach((it, i) => {
    if (it.throwable == null) return;
    const id = it.id ?? '';
    const t = { ...THROWABLE_FIELD_DEFAULTS, ...stripUndefined(it.throwable) };
    const where = `Throwable '${id}'`;
    if (it.weapon || it.armor)
      err('items', i, 'throwable', `Item '${id}' can't be a throwable and a weapon or armor.`);
    const kind = t.kind ?? '';
    if (!(THROWABLE_KINDS as readonly string[]).includes(kind)) {
      err(
        'items',
        i,
        'throwable.kind',
        `${where} has unknown kind '${kind}' (use ${THROWABLE_KINDS.join(', ')}).`,
      );
      return;
    }
    const L = THROWABLE_LIMITS;
    const ranges: [keyof ThrowableDef, number, number][] = [
      ['throwRangeM', 1, L.maxThrowRangeM],
      ['fuseS', 0, L.maxFuseS],
      ['bounce', 0, L.maxBounce],
      ['radiusM', 0, L.maxRadiusM],
      ['damage', 0, L.maxDamage],
      ['falloffMin', 0, 1],
      ['knockbackM', 0, 3],
      ['noiseRadiusM', 0, L.maxNoiseRadiusM],
      ['lureS', 0, L.maxLureS],
      ['pulseS', 0, L.maxLureS],
      ['fireS', 0, L.maxFireS],
      ['fireDps', 0, L.maxFireDps],
      ['lightRadiusM', 0, L.maxRadiusM],
      ['friendlyFireMult', 0, 1],
    ];
    for (const [k, min, max] of ranges) {
      const v = num(t[k] as number | null | undefined);
      if (!(v >= min && v <= max))
        err('items', i, `throwable.${k}`, `${where} needs ${k} in ${min}..${max}.`);
    }
    const e = (k: string, message: string) => err('items', i, `throwable.${k}`, message);
    const radiusM = num(t.radiusM);
    const damage = num(t.damage);
    const fireDps = num(t.fireDps);
    if (kind === 'lure') {
      if (!(num(t.lureS) > 0)) e('lureS', `${where} is a lure and needs lureS above 0.`);
      if (!(num(t.noiseRadiusM) > 0))
        e('noiseRadiusM', `${where} is a lure and needs noiseRadiusM above 0.`);
      if (damage > 0 || fireDps > 0) e('damage', `${where} is a lure and can't do damage.`);
    } else if (kind === 'blast') {
      if (!(radiusM > 0)) e('radiusM', `${where} is a blast and needs radiusM above 0.`);
      if (damage <= 0) e('damage', `${where} is a blast and needs damage above 0.`);
    } else {
      if (!(radiusM > 0)) e('radiusM', `${where} is a fire and needs radiusM above 0.`);
      if (!(num(t.fireS) > 0)) e('fireS', `${where} is a fire and needs fireS above 0.`);
      if (fireDps <= 0) e('fireDps', `${where} is a fire and needs fireDps above 0.`);
    }
  });

  // Combat HUD overhaul keys (ContentValidator.CombatHud.cs, right after the throwables).
  validateCombatHud(b, err);

  // Recruit roles are retired (card 11, ADR 0044): backgrounds replace them and nothing reads recruitRoles any more.
  if ((b.recruitRoles ?? []).length > 0)
    err('recruitRoles', undefined, '', 'recruitRoles is retired: use backgrounds.');

  const s = b.survival;
  if (s) {
    stacks('survival', undefined, 'starterCarried', s.starterCarried, 'Starter carried');
    stacks('survival', undefined, 'starterBase', s.starterBase, 'Starter base');
    (s.foodItemIds ?? []).forEach((id, j) => {
      if (!items.has(id))
        err('survival', undefined, `foodItemIds.${j}`, `Unknown food item '${id}'.`);
    });
    (s.waterItemIds ?? []).forEach((id, j) => {
      if (!items.has(id))
        err('survival', undefined, `waterItemIds.${j}`, `Unknown water item '${id}'.`);
    });
    for (const k of SURVIVAL_NUMBERS) notNegative('survival', undefined, k, s[k], 'Survival rules');
  }

  validateBases(b, err);

  validateTeams(b, { items, tables, categories, roles, traits }, traitsList, err, ids);

  // Survivor conditions (ADR 0031, ContentValidator.Conditions.cs): after squad rules, before the bosses, like the server.
  validateConditions(b, items, modules, modulesList, err);

  // Bosses (ADR 0028, ContentValidator.Bosses.cs). Absent = an empty list (older bundles); explicit null is missing.
  // Each boss is checked with its C# defaults filled in, like the server's parser.
  if (b.bosses === null) err('bosses', undefined, '', "Bundle is missing 'bosses'.");
  else {
    const bossList = b.bosses ?? [];
    bossList.forEach((x, i) => {
      if (x == null) err('bosses', i, '', "Bundle has an empty entry in 'bosses'.");
    });
    ids('bosses', bossList, 'boss');
    const slugs = new Set<string>();
    const prefixes = new Set<string>();
    bossList.forEach((raw, i) => {
      if (raw == null) return;
      const x = withDefaults(bossDefaults(), raw);
      const where = `Boss '${x.id ?? ''}'`;
      const fail = (path: string, message: string) => err('bosses', i, path, message);
      const slug = x.slug ?? '';
      if (!isSlug(slug))
        fail('slug', `${where} needs a slug of lowercase letters, digits and single hyphens.`);
      else if (slugs.has(slug)) fail('slug', `Duplicate boss slug '${slug}'.`);
      else slugs.add(slug);
      const prefix = x.codePrefix ?? '';
      if (!/^[A-Z]{2,12}$/.test(prefix))
        fail('codePrefix', `${where} needs a codePrefix of 2-12 capital letters.`);
      else if (prefixes.has(prefix)) fail('codePrefix', `Duplicate boss codePrefix '${prefix}'.`);
      else prefixes.add(prefix);
      if (isBlank(x.name)) fail('name', `${where} needs a name.`);
      if (num(x.difficulty) < 1 || num(x.difficulty) > 5)
        fail('difficulty', `${where} needs difficulty 1..5.`);
      if (num(x.recommendedTeam) < 1)
        fail('recommendedTeam', `${where} recommendedTeam must be at least 1.`);
      if (!(BOSS_STATUSES as readonly string[]).includes(x.status ?? ''))
        fail('status', `${where} has unknown status '${x.status ?? ''}'.`);
      notNegative('bosses', i, 'energyCost', x.energyCost, where);
      notNegative('bosses', i, 'cooldownHours', x.cooldownHours, where);
      notNegative('bosses', i, 'maxHealth', x.maxHealth, where);
      notNegative('bosses', i, 'headshotMult', x.headshotMult, where);
      if (!isChance(num(x.stealthOpenerFrac)))
        fail('stealthOpenerFrac', `${where} needs stealthOpenerFrac in 0..1.`);
      if (x.firstClearRewards === null)
        fail('firstClearRewards', `${where} has a null 'firstClearRewards' list.`);
      if (num(x.arenaRooms) < 1) fail('arenaRooms', `${where} arenaRooms must be at least 1.`);
      notNegative('bosses', i, 'cacheLootRolls', x.cacheLootRolls, where);
      notNegative('bosses', i, 'repeatRolls', x.repeatRolls, where);
      notNegative('bosses', i, 'minClearSeconds', x.minClearSeconds, where);
      if (x.cacheLootTableId && !tables.has(x.cacheLootTableId))
        fail('cacheLootTableId', `${where} uses unknown loot table '${x.cacheLootTableId}'.`);
      if (x.repeatLootTableId && !tables.has(x.repeatLootTableId))
        fail('repeatLootTableId', `${where} uses unknown loot table '${x.repeatLootTableId}'.`);
      stacks('bosses', i, 'firstClearRewards', x.firstClearRewards, `${where} first clear reward`);
      if (x.playable && x.status !== 'live')
        fail('playable', `${where} can only be playable while live.`);
      if (x.playable && num(x.maxHealth) < 1)
        fail('maxHealth', `${where} is playable but has no maxHealth.`);
      if (x.playable && !x.cacheLootTableId)
        fail('cacheLootTableId', `${where} is playable but has no cacheLootTableId.`);
      bossFight(x, where, fail);
    });
  }

  /**
   * Mirrors `ContentValidator.Fight` (raid overhaul Phase 4): the arena, antechamber, fight numbers, plates, attacks and
   * phases. Nested entries are checked with their C# defaults filled in; a null list counts as empty.
   */
  function bossFight(x: BossDef, where: string, fail: (path: string, message: string) => void) {
    const nn = (path: string, value: number | null | undefined, at: string, field: string) => {
      if (num(value) < 0) fail(path, `${at} ${field} is negative.`);
    };
    const atLeast = (
      path: string,
      value: number | null | undefined,
      min: number,
      at: string,
      field: string,
    ) => {
      if (num(value) < min) fail(path, `${at} ${field} must be at least ${min}.`);
    };
    // An explicit null list (hand-edited JSON) is an error, then checked as empty.
    const nullList = <T>(
      list: T[] | null | undefined,
      at: string,
      path: string,
      name: string,
    ): T[] => {
      if (list === null) fail(path, `${at} has a null '${name}' list.`);
      return list ?? [];
    };
    const arenaId = x.arenaId ?? '';
    const antechamber = nullList(
      x.antechamberZombieIds,
      where,
      'antechamberZombieIds',
      'antechamberZombieIds',
    );
    const plates = nullList(x.plates, where, 'plates', 'plates');
    const phases = nullList(x.phases, where, 'phases', 'phases');
    const attacks = nullList(x.attacks, where, 'attacks', 'attacks');
    if (arenaId && !(BOSS_ARENA_IDS as readonly string[]).includes(arenaId))
      fail('arenaId', `${where} has unknown arenaId '${arenaId}'.`);
    if (x.playable && !arenaId) fail('arenaId', `${where} is playable but has no arenaId.`);
    if (x.playable && x.phases !== null && phases.length === 0)
      fail('phases', `${where} is playable but has no phases.`);
    if (antechamber.length > 3)
      fail('antechamberZombieIds', `${where} has more than 3 antechamberZombieIds.`);
    antechamber.forEach((z, j) => {
      if (!zombies.has(z ?? ''))
        fail(
          `antechamberZombieIds.${j}`,
          `${where} uses unknown zombie '${z ?? ''}' in the antechamber.`,
        );
    });

    nn('modelScale', x.modelScale, where, 'modelScale');
    nn('moveSpeedMps', x.moveSpeedMps, where, 'moveSpeedMps');
    nn('sightRangeM', x.sightRangeM, where, 'sightRangeM');
    nn('hearingMult', x.hearingMult, where, 'hearingMult');
    nn('lureMult', x.lureMult, where, 'lureMult');
    if (!isChance(num(x.knockbackResist)))
      fail('knockbackResist', `${where} needs knockbackResist in 0..1.`);
    if (num(x.weakSpotMult) < 1) fail('weakSpotMult', `${where} needs weakSpotMult of at least 1.`);
    if (num(x.weakSpotArcDeg) < 0 || num(x.weakSpotArcDeg) > 360)
      fail('weakSpotArcDeg', `${where} needs weakSpotArcDeg in 0..360.`);
    if (num(x.weakSpotMult) > 1 && num(x.weakSpotArcDeg) === 0)
      fail('weakSpotArcDeg', `${where} has a weakSpotMult but no weakSpotArcDeg.`);
    if (num(x.weakSpotArcDeg) > 0 && num(x.weakSpotArcDeg) <= 360 && num(x.weakSpotMult) === 1)
      fail('weakSpotMult', `${where} has a weakSpotArcDeg but a weakSpotMult of 1.`);
    if (num(x.exposedMult) < 1) fail('exposedMult', `${where} needs exposedMult of at least 1.`);
    if (num(x.headshotMult) >= 0 && num(x.headshotMult) < 1 && x.playable)
      fail('headshotMult', `${where} needs headshotMult of at least 1.`);
    if (num(x.stealthOpenerFrac) > 0.5 && num(x.stealthOpenerFrac) <= 1)
      fail(
        'stealthOpenerFrac',
        `${where} needs stealthOpenerFrac of at most 0.5 (a stealth attack never kills a boss).`,
      );
    nn('stealthStaggerS', x.stealthStaggerS, where, 'stealthStaggerS');
    const speakers = num(x.speakerCount);
    nn('speakerCount', speakers, where, 'speakerCount');
    if (speakers > 5) fail('speakerCount', `${where} has more than 5 speakers.`);
    if (speakers > 0 && num(x.speakerHealth) < 1)
      fail('speakerHealth', `${where} has speakers without speakerHealth.`);
    nn('speakerRingS', x.speakerRingS, where, 'speakerRingS');
    if (!isChance(num(x.speakerHordeFrac)))
      fail('speakerHordeFrac', `${where} needs speakerHordeFrac in 0..1.`);

    const plateIds = new Set<string>();
    plates.forEach((raw, j) => {
      const base = `plates.${j}`;
      const p = withDefaults(bossPlateDefaults(), raw);
      if (p == null || !p.id) {
        fail(base, `${where} has a plate without an id.`);
        return;
      }
      if (plateIds.has(p.id)) fail(`${base}.id`, `${where} has duplicate plate '${p.id}'.`);
      plateIds.add(p.id);
      const zone = p.zone ?? '';
      if (!(BOSS_PLATE_ZONES as readonly string[]).includes(zone))
        fail(`${base}.zone`, `${where} plate '${p.id}' has unknown zone '${zone}'.`);
      atLeast(`${base}.health`, p.health, 1, `${where} plate '${p.id}'`, 'health');
    });

    const attackIds = new Set<string>();
    attacks.forEach((raw, j) => {
      const base = `attacks.${j}`;
      const a = withDefaults(bossAttackDefaults(), raw);
      if (a == null || !a.id) {
        fail(base, `${where} has an attack without an id.`);
        return;
      }
      const at = `${where} attack '${a.id}'`;
      if (attackIds.has(a.id)) fail(`${base}.id`, `${where} has duplicate attack '${a.id}'.`);
      attackIds.add(a.id);
      const kind = a.kind ?? '';
      if (!(BOSS_ATTACK_KINDS as readonly string[]).includes(kind))
        fail(`${base}.kind`, `${at} has unknown kind '${kind}'.`);
      for (const k of [
        'windupS',
        'rangeM',
        'damage',
        'stunS',
        'knockbackM',
        'cooldownS',
        'puddleS',
        'puddleDps',
        'slowS',
      ] as const)
        nn(`${base}.${k}`, a[k], at, k);
      if (!isChance(num(a.slowMult))) fail(`${base}.slowMult`, `${at} needs slowMult in 0..1.`);
      atLeast(`${base}.count`, a.count, 1, at, 'count');
      const line = kind === 'lunge' || kind === 'charge';
      const circle = kind === 'scream' || kind === 'throw' || kind === 'spit' || kind === 'sweep';
      if (line && num(a.lengthM) <= 0) fail(`${base}.lengthM`, `${at} needs a lengthM above 0.`);
      if (circle && num(a.radiusM) <= 0) fail(`${base}.radiusM`, `${at} needs a radiusM above 0.`);
      if (kind === 'sweep' && !(num(a.arcDeg) >= 1 && num(a.arcDeg) <= 360))
        fail(`${base}.arcDeg`, `${at} needs arcDeg in 1..360.`);
      if (kind === 'charge' && num(a.speedMps) <= 0)
        fail(`${base}.speedMps`, `${at} needs a speedMps above 0.`);
    });

    phases.forEach((raw, i) => {
      const base = `phases.${i}`;
      const at = `${where} phase ${i + 1}`;
      const p = withDefaults(bossPhaseDefaults(), raw);
      if (p == null) {
        fail(base, `${at} is empty.`);
        return;
      }
      const frac = num(p.atHealthFrac);
      if (frac < 0 || frac > 1) fail(`${base}.atHealthFrac`, `${at} needs atHealthFrac in 0..1.`);
      if (i > 0 && frac <= 0 && !p.plateBroken && !p.afterSpeakers)
        fail(base, `${at} never begins: give it atHealthFrac, plateBroken or afterSpeakers.`);
      if (p.plateBroken && !plateIds.has(p.plateBroken))
        fail(`${base}.plateBroken`, `${at} waits for unknown plate '${p.plateBroken}'.`);
      if (p.afterSpeakers && speakers <= 0)
        fail(`${base}.afterSpeakers`, `${at} waits for speakers the boss doesn't have.`);
      nullList(p.attacks, at, `${base}.attacks`, 'attacks').forEach((a, j) => {
        if (!attackIds.has(a ?? ''))
          fail(`${base}.attacks.${j}`, `${at} uses unknown attack '${a ?? ''}'.`);
      });
      if (p.addsZombieId && !zombies.has(p.addsZombieId))
        fail(`${base}.addsZombieId`, `${at} summons unknown zombie '${p.addsZombieId}'.`);
      if (!(num(p.speedMult) > 0)) fail(`${base}.speedMult`, `${at} needs speedMult above 0.`);
      for (const k of [
        'addsPerWave',
        'addsIntervalS',
        'addsOnNoiseM',
        'addsMax',
        'visionRadiusM',
        'channelS',
        'channelInterruptDamage',
      ] as const)
        nn(`${base}.${k}`, p[k], at, k);
      if (!isChance(num(p.channelHordePerS)))
        fail(`${base}.channelHordePerS`, `${at} needs channelHordePerS in 0..1.`);
      nn(`${base}.timerS`, p.timerS, at, 'timerS');
      if (!isChance(num(p.timerHealFrac)))
        fail(`${base}.timerHealFrac`, `${at} needs timerHealFrac in 0..1.`);
    });
  }

  // Scan triggers, missions and cosmetics (ADR 0029, ContentValidator.Triggers.cs), after the bosses like the server.
  validateTriggers(b, { items, tables, zombies, categories }, err, warn, ids);

  // Stories & Tech Step 1, in the server's order after the triggers: ContentValidator.Beats.cs (beatChains, allies, story),
  // .Panels.cs (panelRules, pronounSets, npcs) and .Scenarios.cs (scenarios, setPieces, worldFlags, missions[].stages).
  validateBeats(b, { items, tables, zombies, roles }, err, warn, ids);
  validatePanels(b, err, ids);
  validateScenarios(b, { items, tables, zombies, categories }, err, warn, ids);

  // Stories & Tech Step 2, in the server's order after the scenarios (ContentValidator.cs): Chance, Rescues, Occupied, Holdouts, Tech,
  // KeyParts. One module per C# partial.
  validateChance(b, err, warn, ids);
  validateRescues(b, err, warn, ids);
  validateOccupied(b, err, warn, ids);
  validateHoldouts(b, err, warn, ids);
  validateTech(b, err, warn, ids);
  validateKeyParts(b, err, warn, ids);

  // Stories & Tech Step 3, in the server's order after the key parts (ContentValidator.cs): Arena (S3-ARS), Overwatch (S3-OWS).
  validateArena(b, err, warn, ids);
  validateOverwatch(b, err, warn, ids);

  // Stories & Tech Step 4, in the server's order after the overwatch (ContentValidator.cs): Story (S4-SRV; arcs, storyRules,
  // missions[].story, scenarios[].lose[].onFail).
  validateStoryIssues(b, err);

  // The homebase redesign and Outta-Ten, in the server's order after the story (ContentValidator.cs): Effects (card 18), Skills
  // (ADR 0066), Names (CONTRACT §13.5), HomeSkills (card 5), Inventory (card 6), Home (card 7).
  validateEffects(b, err, ids);
  validateSkills(b, err);
  validateNames(b, err);
  validateHomeSkills(b, err);
  validateInventory(b, err);
  validateHome(b, err);

  // Location themes (ADR 0052), last like the server (ContentValidator.Themes.cs).
  validateThemes(b, err);

  // Health intake (CONTRACT §7), after the themes like the server (ContentValidator.Health.cs).
  validateHealth(b, err, warn);

  // WIND (ADR 0073, ContentValidator.Wind.cs): raidRules.wind, winds[], the wind fields and the expand step's warnings.
  validateWind(b, err);
  windWarnings(b, warn);

  // Day Zero (ADR 0074, ContentValidator.Tutorial.cs): tutorialRules, the kit, tutorial scenarios, no tutorial.* writes.
  validateTutorial(b, err);

  // Weapon sounds (audio plan §4 item 5): ContentValidator.Warnings only (ContentValidator.Audio.cs).
  validateWeaponAudio(b, warn);

  for (const [k, v] of Object.entries(b.noiseRadii ?? {}))
    if (v < 0) warn('noiseRadii', undefined, k, 'Should not be negative.');
  for (const a of NOISE_ACTIONS)
    if (!(a in (b.noiseRadii ?? {}))) warn('noiseRadii', undefined, a, `No radius for '${a}'.`);

  return issues;
}
